import { motion, useReducedMotion } from "framer-motion";

/**
 * Cinematic atmosphere for the landing page hero.
 * Optimized for scroll performance:
 * - uses hardware-accelerated transforms only
 * - reduces heavy blur/filters on small screens
 * - promotes layers to avoid main-thread repaints
 */
export function LandingAtmosphere() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Base sky — warm cream */}
      <div
        className="absolute inset-0"
        style={{
          background: "hsl(40 30% 97%)",
          transform: "translateZ(0)",
        }}
      />

      {/* Brand color wash */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: "hsl(var(--primary) / 0.04)",
          transform: "translateZ(0)",
        }}
      />

      {/* Brand primary glow, top-right */}
      <motion.div
        initial={prefersReducedMotion ? { opacity: 0.55 } : { opacity: 0.5 }}
        animate={
          prefersReducedMotion
            ? { opacity: 0.55 }
            : { opacity: [0.4, 0.6, 0.4] }
        }
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-20 -top-20 h-[400px] w-[400px] rounded-full blur-[100px] md:-right-40 md:-top-40 md:h-[800px] md:w-[800px] md:blur-[120px]"
        style={{
          backgroundColor: "hsl(var(--primary) / 0.18)",
          willChange: "opacity",
          transform: "translateZ(0)",
        }}
      />

      {/* Brand secondary glow, left */}
      <motion.div
        initial={prefersReducedMotion ? { opacity: 0.55 } : { opacity: 0.5 }}
        animate={
          prefersReducedMotion
            ? { opacity: 0.55 }
            : { opacity: [0.4, 0.6, 0.4] }
        }
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute -left-16 top-[10%] h-[350px] w-[350px] rounded-full blur-[90px] md:-left-32 md:h-[700px] md:w-[700px] md:blur-[110px]"
        style={{
          backgroundColor: "hsl(var(--secondary) / 0.15)",
          willChange: "opacity",
          transform: "translateZ(0)",
        }}
      />

      {/* Brand primary depth, bottom-right */}
      <div
        className="absolute -bottom-24 right-[-10%] h-[300px] w-[300px] rounded-full blur-[80px] md:-bottom-48 md:h-[600px] md:w-[600px] md:blur-[100px]"
        style={{
          backgroundColor: "hsl(var(--primary) / 0.10)",
          willChange: "opacity",
          transform: "translateZ(0)",
        }}
      />

      {/* Drifting cloud forms */}
      <motion.div
        initial={{ x: 0 }}
        animate={
          prefersReducedMotion
            ? { x: 0 }
            : { x: [0, 60, 0] }
        }
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[5%] top-[18%] h-32 w-[320px] rounded-full bg-white/60 blur-[60px] md:h-48 md:w-[620px] md:blur-[80px]"
        style={{ willChange: "transform", transform: "translateZ(0)" }}
      />
      <motion.div
        initial={{ x: 0 }}
        animate={
          prefersReducedMotion
            ? { x: 0 }
            : { x: [0, -40, 0] }
        }
        transition={{ duration: 40, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[8%] top-[42%] h-36 w-[360px] rounded-full bg-white/50 blur-[70px] md:h-56 md:w-[680px] md:blur-[90px]"
        style={{ willChange: "transform", transform: "translateZ(0)" }}
      />
      <div
        className="absolute bottom-[5%] left-1/2 h-44 w-[600px] -translate-x-1/2 rounded-[50%] bg-white/45 blur-[80px] md:h-80 md:w-[1200px] md:blur-[110px]"
        style={{ transform: "translateX(-50%) translateZ(0)", willChange: "opacity" }}
      />

      {/* Infrastructure-style grid overlay (very subtle) */}
      <div
        className="absolute inset-0 opacity-[0.02] md:opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "120px 120px",
          transform: "translateZ(0)",
        }}
      />

      {/* Horizon line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[1px]"
        style={{
          backgroundColor: "hsl(var(--secondary) / 0.25)",
          transform: "translateZ(0)",
        }}
      />

      {/* Film grain texture — disabled on touch/mouse heavy scroll; only tiny noise tile */}
      <div
        className="absolute inset-0 opacity-[0.015] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          transform: "translateZ(0)",
        }}
      />
    </div>
  );
}
